import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';


import { useAuth } from '../../contexts/AuthContext';
import ProfileHeader from './components/ProfileHeader';
import ProfileForm from './components/ProfileForm';
import ProfileSecuritySection from './components/ProfileSecuritySection';

const UserProfile = () => {
  const { user, userProfile, updateProfile } = useAuth();
  const [notification, setNotification] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    nickname: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (userProfile) {
      setFormData({
        firstName: userProfile?.first_name || '',
        lastName: userProfile?.last_name || '',
        nickname: userProfile?.nickname || ''
      });
    }
  }, [userProfile]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const validate = () => {
    const errs = {};
    if (!formData?.firstName?.trim()) errs.firstName = 'First name is required';
    if (!formData?.lastName?.trim()) errs.lastName = 'Last name is required';
    setErrors(errs);
    return Object.keys(errs)?.length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      const { error } = await updateProfile({
        first_name: formData?.firstName?.trim(),
        last_name: formData?.lastName?.trim(),
        nickname: formData?.nickname?.trim() || null,
        full_name: `${formData?.firstName?.trim()} ${formData?.lastName?.trim()}`
      });
      if (error) throw error;
      showNotification('Profile updated successfully');
    } catch (err) {
      showNotification(err?.message || 'Failed to update profile', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-background">
        {/* Notification Toast */}
        {notification && (
          <div className={`fixed top-4 right-4 z-[300] flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border transition-all ${
            notification?.type === 'success' ?'bg-green-50 border-green-200 text-green-800' :'bg-red-50 border-red-200 text-red-800'
          }`}>
            <Icon name={notification?.type === 'success' ? 'CheckCircle2' : 'AlertCircle'} size={18} />
            <span className="text-sm font-medium">{notification?.message}</span>
            <button onClick={() => setNotification(null)} className="ml-2 hover:opacity-70">
              <Icon name="X" size={14} />
            </button>
          </div>
        )}

        {/* Page Header */}
        <div className="bg-card border-b border-border">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-accent/10 flex items-center justify-center">
                <Icon name="User" size={18} className="text-accent" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground">My Profile</h1>
                <p className="text-sm text-muted-foreground">Manage your personal information</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-5">
          {/* Profile Header Card */}
          <ProfileHeader userProfile={userProfile} user={user} />

          {/* Edit Form Card */}
          <ProfileForm
            formData={formData}
            errors={errors}
            onChange={handleChange}
            onSave={handleSave}
            isSaving={isSaving}
          />

          {/* Security Section */}
          <ProfileSecuritySection userProfile={userProfile} />
        </div>
      </div>
    </MainLayout>
  );
};

export default UserProfile;
